/**
 * Ideology Utility Functions
 *
 * Pure functions for working with ideology vectors in the game.
 * Used for calculating ideological distance, averaging positions,
 * shifting ideology, and validating vectors.
 */

import {
  IDEOLOGY_AXES,
  IDEOLOGY_MIN,
  IDEOLOGY_MAX,
  IDEOLOGY_AXIS_COUNT,
  DEFAULT_SALIENCE,
  MAX_IDEOLOGY_DISTANCE,
  POSITION_THRESHOLDS,
  type IdeologyVector,
} from '@/constants/ideology';

/**
 * Calculate weighted Euclidean distance between two ideology vectors.
 * Lower distance = more ideologically similar.
 *
 * @param a - First ideology vector
 * @param b - Second ideology vector
 * @param weights - Optional salience weights (defaults to equal weights)
 * @returns Weighted Euclidean distance (0 to ~529 with default weights)
 *
 * @example
 * const partyPos: IdeologyVector = [50, -30, 20, 0, 40, -10, 30];
 * const voterPos: IdeologyVector = [40, -20, 25, 5, 35, -5, 25];
 * const distance = calculateDistance(partyPos, voterPos);
 */
export function calculateDistance(
  a: IdeologyVector,
  b: IdeologyVector,
  weights: IdeologyVector = DEFAULT_SALIENCE
): number {
  let sum = 0;
  for (let i = 0; i < IDEOLOGY_AXIS_COUNT; i++) {
    const diff = a[i] - b[i];
    sum += weights[i] * diff * diff;
  }
  return Math.sqrt(sum);
}

/**
 * Calculate unweighted Euclidean distance between two ideology vectors.
 * Equivalent to calculateDistance with all weights = 1.
 *
 * @param a - First ideology vector
 * @param b - Second ideology vector
 * @returns Euclidean distance (0 to ~529)
 */
export function calculateRawDistance(
  a: IdeologyVector,
  b: IdeologyVector
): number {
  let sum = 0;
  for (let i = 0; i < IDEOLOGY_AXIS_COUNT; i++) {
    const diff = a[i] - b[i];
    sum += diff * diff;
  }
  return Math.sqrt(sum);
}

/**
 * Calculate the average ideology of multiple vectors.
 * Useful for determining party ideology from members or coalition positions.
 *
 * @param vectors - Array of ideology vectors to average
 * @returns Average ideology vector, or neutral vector if empty array
 *
 * @example
 * const members = [
 *   [50, 30, 20, 10, 40, -10, 30],
 *   [60, 40, 30, 20, 50, 0, 40],
 * ];
 * const partyIdeology = calculateAverage(members);
 */
export function calculateAverage(vectors: IdeologyVector[]): IdeologyVector {
  if (vectors.length === 0) {
    return createNeutralVector();
  }

  const sum: number[] = [0, 0, 0, 0, 0, 0, 0];

  for (const vector of vectors) {
    for (let i = 0; i < IDEOLOGY_AXIS_COUNT; i++) {
      sum[i] += vector[i];
    }
  }

  return sum.map((val) => val / vectors.length) as IdeologyVector;
}

/**
 * Shift an ideology vector on a specific axis by an amount.
 * The result is clamped to valid range (-100 to 100).
 *
 * @param vector - Original ideology vector
 * @param axisIndex - Index of axis to shift (0-6)
 * @param amount - Amount to shift (positive = right, negative = left)
 * @returns New ideology vector with shifted value
 *
 * @example
 * const original: IdeologyVector = [0, 0, 0, 0, 0, 0, 0];
 * const shifted = shiftIdeology(original, 0, 25); // Shift economic axis +25
 * // Result: [25, 0, 0, 0, 0, 0, 0]
 */
export function shiftIdeology(
  vector: IdeologyVector,
  axisIndex: number,
  amount: number
): IdeologyVector {
  if (axisIndex < 0 || axisIndex >= IDEOLOGY_AXIS_COUNT) {
    throw new Error(
      `Invalid axis index: ${axisIndex}. Must be 0-${IDEOLOGY_AXIS_COUNT - 1}`
    );
  }

  const result = [...vector] as IdeologyVector;
  result[axisIndex] = clampValue(result[axisIndex] + amount);
  return result;
}

/**
 * Clamp a single value to the valid ideology range.
 *
 * @param value - Value to clamp
 * @returns Value clamped to -100 to 100
 */
function clampValue(value: number): number {
  return Math.max(IDEOLOGY_MIN, Math.min(IDEOLOGY_MAX, value));
}

/**
 * Clamp all values in a vector to valid range (-100 to 100).
 *
 * @param vector - Ideology vector to clamp
 * @returns New vector with all values clamped
 */
export function clampVector(vector: IdeologyVector): IdeologyVector {
  return vector.map(clampValue) as IdeologyVector;
}

/**
 * Create a neutral (centrist) ideology vector with all zeros.
 *
 * @returns Neutral ideology vector [0, 0, 0, 0, 0, 0, 0]
 */
export function createNeutralVector(): IdeologyVector {
  return [0, 0, 0, 0, 0, 0, 0];
}

/**
 * Create a random ideology vector within given bounds.
 * Useful for generating NPC parties or cohorts.
 *
 * @param min - Minimum value for each axis (default -100)
 * @param max - Maximum value for each axis (default 100)
 * @returns Random ideology vector
 */
export function createRandomVector(
  min: number = IDEOLOGY_MIN,
  max: number = IDEOLOGY_MAX
): IdeologyVector {
  const clampedMin = Math.max(IDEOLOGY_MIN, min);
  const clampedMax = Math.min(IDEOLOGY_MAX, max);
  const range = clampedMax - clampedMin;

  return Array.from(
    { length: IDEOLOGY_AXIS_COUNT },
    () => Math.floor(Math.random() * (range + 1)) + clampedMin
  ) as IdeologyVector;
}

/**
 * Check if a value is a valid ideology vector.
 * Valid vectors have exactly 7 elements, all numbers within -100 to 100.
 *
 * @param vector - Value to check
 * @returns True if valid ideology vector
 */
export function isValidVector(vector: unknown): vector is IdeologyVector {
  if (!Array.isArray(vector)) {
    return false;
  }

  if (vector.length !== IDEOLOGY_AXIS_COUNT) {
    return false;
  }

  return vector.every(
    (val) =>
      typeof val === 'number' &&
      !isNaN(val) &&
      val >= IDEOLOGY_MIN &&
      val <= IDEOLOGY_MAX
  );
}

/**
 * Calculate similarity score (0-100) from distance.
 * Inverse relationship: lower distance = higher similarity.
 *
 * @param distance - Euclidean distance between vectors
 * @returns Similarity score from 0 (opposite) to 100 (identical)
 *
 * @example
 * const distance = calculateDistance(partyPos, voterPos);
 * const similarity = distanceToSimilarity(distance);
 * // similarity of 80 means "very aligned", 20 means "quite different"
 */
export function distanceToSimilarity(distance: number): number {
  // Clamp distance to valid range
  const clampedDistance = Math.max(0, Math.min(MAX_IDEOLOGY_DISTANCE, distance));
  // Convert to 0-100 similarity (100 = identical, 0 = maximally different)
  return Math.round((1 - clampedDistance / MAX_IDEOLOGY_DISTANCE) * 100);
}

/**
 * Calculate per-axis distances between two ideology vectors.
 * Returns an array of 7 absolute differences (one per axis).
 *
 * @param a - First ideology vector
 * @param b - Second ideology vector
 * @returns Array of 7 distances (0 to 200 each)
 *
 * @example
 * const partyPos: IdeologyVector = [50, -30, 20, 0, 40, -10, 30];
 * const voterPos: IdeologyVector = [40, -20, 25, 5, 35, -5, 25];
 * const axisDistances = calculateAxisDistance(partyPos, voterPos);
 * // Result: [10, 10, 5, 5, 5, 5, 5]
 */
export function calculateAxisDistance(
  a: IdeologyVector,
  b: IdeologyVector
): number[] {
  const distances: number[] = [];
  for (let i = 0; i < IDEOLOGY_AXIS_COUNT; i++) {
    distances.push(Math.abs(a[i] - b[i]));
  }
  return distances;
}

/**
 * Normalize a raw distance to a 0-100 scale for display.
 * 0 = identical, 100 = maximum divergence.
 *
 * @param distance - Raw Euclidean distance between vectors
 * @returns Normalized distance from 0 (identical) to 100 (max divergence)
 *
 * @example
 * const distance = calculateRawDistance(partyPos, voterPos);
 * const normalized = normalizeDistance(distance);
 * // normalized of 15 means "15% different" or "85% similar"
 */
export function normalizeDistance(distance: number): number {
  const clampedDistance = Math.max(0, Math.min(MAX_IDEOLOGY_DISTANCE, distance));
  return Math.round((clampedDistance / MAX_IDEOLOGY_DISTANCE) * 100);
}

/**
 * Alignment thresholds for categorizing ideological distance.
 */
export const ALIGNMENT_THRESHOLDS = {
  NEARLY_IDENTICAL: 10,  // 0-10%
  CLOSELY_ALIGNED: 25,   // 11-25%
  MODERATE: 50,          // 26-50%
  SIGNIFICANT: 75,       // 51-75%
  // 76-100% = Opposed
} as const;

/**
 * Get a human-readable alignment label based on normalized distance.
 *
 * @param normalizedDistance - Distance on 0-100 scale (0 = identical, 100 = opposed)
 * @returns Human-readable label describing alignment
 *
 * @example
 * getAlignmentLabel(5);   // "Nearly Identical"
 * getAlignmentLabel(20);  // "Closely Aligned"
 * getAlignmentLabel(40);  // "Moderate Differences"
 * getAlignmentLabel(65);  // "Significantly Different"
 * getAlignmentLabel(85);  // "Opposed"
 */
export function getAlignmentLabel(normalizedDistance: number): string {
  if (normalizedDistance <= ALIGNMENT_THRESHOLDS.NEARLY_IDENTICAL) {
    return 'Nearly Identical';
  }
  if (normalizedDistance <= ALIGNMENT_THRESHOLDS.CLOSELY_ALIGNED) {
    return 'Closely Aligned';
  }
  if (normalizedDistance <= ALIGNMENT_THRESHOLDS.MODERATE) {
    return 'Moderate Differences';
  }
  if (normalizedDistance <= ALIGNMENT_THRESHOLDS.SIGNIFICANT) {
    return 'Significantly Different';
  }
  return 'Opposed';
}

/**
 * Get alignment color class based on normalized distance.
 * Useful for styling UI components.
 *
 * @param normalizedDistance - Distance on 0-100 scale
 * @returns Tailwind color classes for text/background
 */
export function getAlignmentColor(normalizedDistance: number): {
  text: string;
  bg: string;
  border: string;
} {
  if (normalizedDistance <= ALIGNMENT_THRESHOLDS.NEARLY_IDENTICAL) {
    return {
      text: 'text-green-600 dark:text-green-400',
      bg: 'bg-green-500',
      border: 'border-green-500',
    };
  }
  if (normalizedDistance <= ALIGNMENT_THRESHOLDS.CLOSELY_ALIGNED) {
    return {
      text: 'text-green-500 dark:text-green-400',
      bg: 'bg-green-400',
      border: 'border-green-400',
    };
  }
  if (normalizedDistance <= ALIGNMENT_THRESHOLDS.MODERATE) {
    return {
      text: 'text-yellow-600 dark:text-yellow-400',
      bg: 'bg-yellow-500',
      border: 'border-yellow-500',
    };
  }
  if (normalizedDistance <= ALIGNMENT_THRESHOLDS.SIGNIFICANT) {
    return {
      text: 'text-orange-600 dark:text-orange-400',
      bg: 'bg-orange-500',
      border: 'border-orange-500',
    };
  }
  return {
    text: 'text-red-600 dark:text-red-400',
    bg: 'bg-red-500',
    border: 'border-red-500',
  };
}

/**
 * Get human-readable position label for a value on an axis.
 *
 * Thresholds:
 * - -100 to -60: "Strongly [Left Label]"
 * - -60 to -20: "Moderately [Left Label]"
 * - -20 to 20: "Centrist"
 * - 20 to 60: "Moderately [Right Label]"
 * - 60 to 100: "Strongly [Right Label]"
 *
 * @param axisIndex - Index of the ideology axis (0-6)
 * @param value - Position value on that axis (-100 to 100)
 * @returns Human-readable position label
 *
 * @example
 * getPositionLabel(0, -80); // "Strongly Free Market"
 * getPositionLabel(1, 20);  // "Slightly Progressive"
 * getPositionLabel(2, 0);   // "Centrist"
 */
export function getPositionLabel(axisIndex: number, value: number): string {
  if (axisIndex < 0 || axisIndex >= IDEOLOGY_AXIS_COUNT) {
    throw new Error(
      `Invalid axis index: ${axisIndex}. Must be 0-${IDEOLOGY_AXIS_COUNT - 1}`
    );
  }

  const axis = IDEOLOGY_AXES[axisIndex];

  if (value <= POSITION_THRESHOLDS.STRONG_LEFT) {
    return `Strongly ${axis.leftLabel}`;
  } else if (value <= POSITION_THRESHOLDS.MODERATE_LEFT) {
    return `Moderately ${axis.leftLabel}`;
  } else if (value < POSITION_THRESHOLDS.MODERATE_RIGHT) {
    return 'Centrist';
  } else if (value < POSITION_THRESHOLDS.STRONG_RIGHT) {
    return `Moderately ${axis.rightLabel}`;
  } else {
    return `Strongly ${axis.rightLabel}`;
  }
}

/**
 * Normalize salience weights so they sum to 1.
 * Useful for weighted calculations where total weight should equal 1.
 *
 * @param weights - Salience weights to normalize
 * @returns Normalized weights that sum to 1
 *
 * @example
 * const weights: IdeologyVector = [2, 2, 1, 1, 1, 1, 1];
 * const normalized = normalizeSalience(weights);
 * // Result: each weight divided by 9 (the sum)
 */
export function normalizeSalience(weights: IdeologyVector): IdeologyVector {
  const sum = weights.reduce((acc, val) => acc + val, 0);

  if (sum === 0) {
    // If all weights are 0, return equal weights
    const equalWeight = 1 / IDEOLOGY_AXIS_COUNT;
    return Array(IDEOLOGY_AXIS_COUNT).fill(equalWeight) as IdeologyVector;
  }

  return weights.map((val) => val / sum) as IdeologyVector;
}

/**
 * Get the axis definition by its key.
 *
 * @param key - Axis key (e.g., 'economic_regulation')
 * @returns Axis definition or undefined if not found
 */
export function getAxisByKey(key: string) {
  return IDEOLOGY_AXES.find((axis) => axis.key === key);
}

/**
 * Get the axis definition by its index.
 *
 * @param index - Axis index (0-6)
 * @returns Axis definition or undefined if invalid index
 */
export function getAxisByIndex(index: number) {
  if (index < 0 || index >= IDEOLOGY_AXIS_COUNT) {
    return undefined;
  }
  return IDEOLOGY_AXES[index];
}
